import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, InputGroup, FormControl, Spinner } from 'react-bootstrap';
import { FaSearch, FaUser, FaBuilding, FaPlus, FaMinus, FaTrash, FaTimes, FaExchangeAlt } from 'react-icons/fa';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import workflowAssignmentService from '../../services/workflowAssignmentService';
import OrganizationalUnitService from '../../services/OrganizationalUnitService';
import WorkflowService from '../../services/workflowService';
import ProfileImage from '../ProfileImage';
import { getIconComponent } from '../IconPicker';
import '../../assets/styles/WorkflowAssignment.css';

// Internal Modal component
const AssignmentModal = ({ isOpen, title, onClose, children, footer }) => {
    console.log('AssignmentModal render - isOpen:', isOpen); // Debug log

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="access-modal-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="access-modal-content"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="access-modal-header">
                            <h3 className="access-modal-title">{title}</h3>
                            <button className="access-modal-close" onClick={onClose}>
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="access-modal-body">
                            {children}
                        </div>
                        
                        {footer && (
                            <div className="access-modal-footer">
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const WorkflowAssignment = ({ darkMode }) => {
    // State for organizational units and workflows
    const [orgUnits, setOrgUnits] = useState([]);
    const [workflows, setWorkflows] = useState([]);
    const [expandedUnits, setExpandedUnits] = useState({});
    const [expandedWorkflows, setExpandedWorkflows] = useState({});
    const [unitSearch, setUnitSearch] = useState('');
    const [workflowSearch, setWorkflowSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [workflowAssignmentInfo, setWorkflowAssignmentInfo] = useState({});
    
    // State for assignment modal
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({
        type: '', // 'user' or 'unit'
        sourceId: null,
        sourceName: '',
        targetId: null,
        targetName: '',
        isExcluded: false,
        excludedFromUnitId: null
    });

    // State for responsive design
    const [isSmallPanel, setIsSmallPanel] = useState(false);
    const [panelWidth, setPanelWidth] = useState(0);
    const [theme, setTheme] = useState('light');

    // Add useEffect to handle theme changes
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    }, [darkMode]);

    // Theme detection effect
    useEffect(() => {
        const checkTheme = () => {
            const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
            setTheme(currentTheme);
        };
        
        checkTheme();
        
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.attributeName === 'data-theme') {
                    checkTheme();
                }
            });
        });
        
        observer.observe(document.documentElement, { attributes: true });
        
        return () => observer.disconnect();
    }, []);

    // Panel size detection for responsive design
    useEffect(() => {
        const checkPanelSize = () => {
            const container = document.querySelector('.workflow-assignment-panel');
            if (container) {
                const width = container.offsetWidth;
                setPanelWidth(width);
                setIsSmallPanel(width < 768);
            }
        };

        checkPanelSize();
        window.addEventListener('resize', checkPanelSize);

        return () => {
            window.removeEventListener('resize', checkPanelSize);
        };
    }, []);

    // Fetch organizational units, workflows on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Fetch organizational units
                const unitsData = await OrganizationalUnitService.getOrganizationChart();
                if (!unitsData || unitsData.length === 0) {
                    setError('No organizational units found');
                    setLoading(false);
                    return;
                }
                setOrgUnits(unitsData);
                
                // Fetch workflows
                const workflowsData = await WorkflowService.getAllWorkflows();
                setWorkflows(workflowsData);
                
                // Initialize expanded states
                const unitExpanded = {};
                const initializeExpandedState = (units) => {
                    units.forEach(unit => {
                        unitExpanded[unit.id] = false;
                        if (unit.children && unit.children.length > 0) {
                            initializeExpandedState(unit.children);
                        }
                    });
                };
                initializeExpandedState(unitsData);
                setExpandedUnits(unitExpanded);
                
                const workflowExpanded = {};
                workflowsData.forEach(workflow => {
                    workflowExpanded[workflow.id] = false;
                });
                setExpandedWorkflows(workflowExpanded);
                
                // After workflows are set, fetch assignments
                if (workflowsData && workflowsData.length > 0) {
                    await fetchAllAssignments();
                }
                
                setLoading(false);
            } catch (err) {
                setError('Failed to load data. Please try again later.');
                setLoading(false);
                toast.error('Failed to load data. Please try again later.');
            }
        };
        
        fetchData();
    }, []);

    // Add effect to update assignment info when workflows change
    useEffect(() => {
        if (workflows && workflows.length > 0) {
            fetchAllAssignments();
        }
    }, [workflows]);

    // Fetch assignments for all workflows
    const fetchAllAssignments = async () => {
        try {
            const assignmentsByWorkflow = {};
            
            for (const workflow of workflows) {
                try {
                    const workflowAssignments = await workflowAssignmentService.getByWorkflowId(workflow.id);
                    if (workflowAssignments) {
                        assignmentsByWorkflow[workflow.id] = workflowAssignments;
                    }
                } catch (err) {
                    toast.error(`Failed to fetch assignments for workflow ${workflow.name}`);
                }
            }

            setWorkflowAssignmentInfo(assignmentsByWorkflow);
        } catch (err) {
            toast.error('Failed to fetch workflow assignments');
        }
    };
    
    // Toggle expanded state for units
    const toggleUnitExpand = (unitId) => {
        setExpandedUnits(prev => ({
            ...prev,
            [unitId]: !prev[unitId]
        }));
    };
    
    // Toggle expanded state for workflows
    const toggleWorkflowExpand = (workflowId) => {
        setExpandedWorkflows(prev => ({
            ...prev,
            [workflowId]: !prev[workflowId]
        }));
    };
    
    // Filter units based on search term
    const filterUnits = (units, searchTerm) => {
        if (!searchTerm) return units;
        
        return units.filter(unit => {
            const matchesName = unit.name.toLowerCase().includes(searchTerm.toLowerCase());
            const hasMatchingChildren = unit.children && filterUnits(unit.children, searchTerm).length > 0;
            const hasMatchingUsers = unit.assignedUsers && unit.assignedUsers.some(user => 
                user.userFullName.toLowerCase().includes(searchTerm.toLowerCase())
            );
            
            return matchesName || hasMatchingChildren || hasMatchingUsers;
        });
    };
    
    // Filter workflows based on search term
    const filterWorkflows = (workflows, searchTerm) => {
        if (!searchTerm) return workflows;
        
        return workflows.filter(workflow => 
            workflow.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    // Handle drag end event
    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;

        console.log('Workflow Assignment - Drag end event:', { source, destination, draggableId });

        if (!destination) {
            console.log('Workflow Assignment - No destination, drag cancelled');
            return;
        }

        const draggedItemType = draggableId.split('-')[0];
        const draggedItemId = draggableId.split('-')[1];

        console.log('Workflow Assignment - Dragged item details:', { draggedItemType, draggedItemId });

        if (destination.droppableId.startsWith('workflow-')) {
            const workflowId = destination.droppableId.replace('workflow-', '');
            const workflow = workflows.find(w => w.id === parseInt(workflowId));

            console.log('Workflow Assignment - Workflow drop detected:', { workflowId, workflow });

            if (draggedItemType === 'unit') {
                const unit = findUnit(orgUnits, draggedItemId);
                console.log('Workflow Assignment - Unit found:', unit);
                if (unit) {
                    setModalData({
                        type: 'unit',
                        sourceId: draggedItemId,
                        sourceName: unit.name,
                        targetId: workflowId,
                        targetName: workflow?.name,
                        isExcluded: false,
                        excludedFromUnitId: null
                    });
                    setShowModal(true);
                } else {
                    console.error('Workflow Assignment - Unit not found for ID:', draggedItemId);
                }
            } else if (draggedItemType === 'user') {
                const user = findUser(orgUnits, draggedItemId);
                console.log('Workflow Assignment - User found:', user);
                if (user) {
                    setModalData({
                        type: 'user',
                        sourceId: draggedItemId,
                        sourceName: user.userFullName,
                        targetId: workflowId,
                        targetName: workflow?.name,
                        isExcluded: false
                    });
                    setShowModal(true);
                } else {
                    console.error('Workflow Assignment - User not found for ID:', draggedItemId);
                }
            }
        } else {
            console.log('Workflow Assignment - Not dropped on workflow area, destination:', destination.droppableId);
        }
    };

    // Helper function to find a unit by id
    const findUnit = (units, id) => {
        for (const unit of units) {
            if (String(unit.id) === String(id)) return unit;
            if (unit.children && unit.children.length > 0) {
                const found = findUnit(unit.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    // Helper function to find a user by id
    const findUser = (units, userId) => {
        for (const unit of units) {
            const unitUsers = unit.assignedUsers || unit.users || [];
            const user = unitUsers.find(u => 
                String(u.userId) === String(userId) || String(u.id) === String(userId)
            );
            if (user) return user;
            
            if (unit.children && unit.children.length > 0) {
                const found = findUser(unit.children, userId);
                if (found) return found;
            }
        }
        return null;
    };

    // Handle assignment creation
    const handleAssignWorkflow = async () => {
        try {
            console.log('Starting assignment creation with modalData:', modalData);

            // Check if assignment already exists
            const workflowAssignments = workflowAssignmentInfo[modalData.targetId] || [];
            console.log('Existing assignments for workflow:', workflowAssignments);

            if (modalData.type === 'unit') {
                const unitExists = workflowAssignments.some(assignment => 
                    assignment.organizationalUnitId === parseInt(modalData.sourceId)
                );
                if (unitExists) {
                    toast.error('This unit already has assignment to this workflow');
                    return;
                }
            } else {
                const userExists = workflowAssignments.some(assignment => 
                    assignment.userId === parseInt(modalData.sourceId)
                );
                if (userExists) {
                    toast.error('This user already has assignment to this workflow');
                    return;
                }
            }

            // Prepare assignment data
            const assignmentData = {
                workflowId: parseInt(modalData.targetId),
                isExcluded: modalData.isExcluded || false,
                excludedFromUnitId: modalData.excludedFromUnitId ? parseInt(modalData.excludedFromUnitId) : null,
                userId: modalData.type === 'user' ? parseInt(modalData.sourceId) : null,
                organizationalUnitId: modalData.type === 'unit' ? parseInt(modalData.sourceId) : null,
                isActive: true
            };

            console.log('Assignment data to be sent:', assignmentData);

            // Call API to create assignment
            const result = await workflowAssignmentService.create(assignmentData);
            console.log('Assignment created successfully:', result);

            // Refresh all workflow assignments
            await fetchAllAssignments();

            // Close modal and show success message
            setShowModal(false);
            toast.success('Workflow assignment created successfully');
        } catch (err) {
            console.error('Error creating workflow assignment:', err);
            toast.error('Failed to create workflow assignment. Please try again.');
        }
    };

    // Render organizational unit tree
    const renderUnitTree = (units, level = 0) => {
        if (!units || units.length === 0) return null;
        
        const filteredUnits = filterUnits(units, unitSearch);
        
        if (filteredUnits.length === 0) return null;
        
        return (
            <ul className="unit-tree" style={{ paddingLeft: level > 0 ? '20px' : '0' }}>
                {filteredUnits.map((unit, index) => {
                    const unitUsers = unit.assignedUsers || unit.users || [];
                    
                    return (
                        <li key={`unit-${unit.id}`} className="unit-item">
                            <div className="unit-header">
                                <button 
                                    className="toggle-btn"
                                    onClick={() => toggleUnitExpand(unit.id)}
                                >
                                    {expandedUnits[unit.id] ? <FaMinus /> : <FaPlus />}
                                </button>
                                <Draggable draggableId={`unit-${unit.id}`} index={index} key={`unit-${unit.id}`}>
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className="unit-label"
                                            style={{
                                                ...provided.draggableProps.style,
                                                backgroundColor: unit.unitTypeColor || '#3498db'
                                            }}
                                        >
                                            <span style={{ marginRight: '5px' }}>
                                                {getIconComponent(unit.unitTypeIcon) || <FaBuilding />}
                                            </span>
                                            {unit.name}
                                        </div>
                                    )}
                                </Draggable>
                            </div>
                            
                            {expandedUnits[unit.id] && (
                                <>
                                    {unitUsers && unitUsers.length > 0 ? (
                                        <ul className="user-list">
                                            {unitUsers.map((user, userIndex) => (
                                                <div key={`user-${user.userId || user.id}`}>
                                                    <Draggable draggableId={`user-${user.userId || user.id}`} index={userIndex}>
                                                        {(provided) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className="user-label"
                                                            >
                                                                <FaUser style={{ marginRight: '5px' }} />
                                                                {user.userFullName || user.fullName || user.name || 'Unknown User'}
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                </div>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="no-users-message">No users assigned to this unit</div>
                                    )}
                                    
                                    {unit.children && unit.children.length > 0 && renderUnitTree(unit.children, level + 1)}
                                </>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    };

    // Render workflow list (similar to desktop list in DesktopManagement)
    const renderWorkflowList = (workflows) => {
        if (!workflows || workflows.length === 0) return null;
        
        const filteredWorkflows = filterWorkflows(workflows, workflowSearch);
        
        if (filteredWorkflows.length === 0) return null;
        
        return filteredWorkflows.map((workflow, index) => (
            <Droppable key={`workflow-${workflow.id}`} droppableId={`workflow-${workflow.id}`}>
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`list-group-item mb-2 workflow-drop-zone ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                        style={{ minHeight: '80px' }}
                    >
                        <div className="d-flex justify-content-between align-items-start">
                            <div className="d-flex align-items-start flex-grow-1">
                                <div className="me-3">
                                    <FaExchangeAlt size={24} style={{ color: '#007bff' }} />
                                </div>
                                <div className="flex-grow-1">
                                    <h5 className="mb-1" style={textStyle}>{workflow.name}</h5>
                                    {!isSmallPanel && workflow.description && (
                                        <p className="mb-1 text-muted">{workflow.description}</p>
                                    )}
                                    <small className="text-muted">Process: {workflow.processTypeName || 'Unknown'}</small>
                                    
                                    {/* Assigned Access Section */}
                                    {workflowAssignmentInfo[workflow.id] && (
                                        <div className="mt-2">
                                            <div className="d-flex flex-wrap gap-1 align-items-center">
                                                {/* Users */}
                                                {workflowAssignmentInfo[workflow.id]
                                                    .filter(assignment => assignment.userId)
                                                    .map(assignment => {
                                                        // Use a consistent user color (blue theme)
                                                        const userColor = '#1976d2';
                                                        const isDarkTheme = theme === 'dark';
                                                        const backgroundColor = isDarkTheme ? `${userColor}30` : `${userColor}20`;
                                                        const textColor = isDarkTheme ? `${userColor}dd` : userColor;
                                                        const borderColor = isDarkTheme ? `${userColor}60` : userColor;
                                                        
                                                        return (
                                                            <span 
                                                                key={assignment.id} 
                                                                className="assignment-tag user-assignment-tag"
                                                                style={{
                                                                    backgroundColor: backgroundColor,
                                                                    color: textColor,
                                                                    borderColor: borderColor
                                                                }}
                                                            >
                                                                <ProfileImage 
                                                                    imageUrl={`/api/users/${assignment.userId}/image`}
                                                                    width={15}
                                                                    height={15}
                                                                />
                                                                <span>
                                                                    {assignment.userName || 'Unknown User'}
                                                                </span>
                                                                <button 
                                                                    className="btn-close" 
                                                                    onClick={() => handleRemoveAssignment(assignment.id)}
                                                                    title="Remove assignment"
                                                                    style={{ color: textColor }}
                                                                ></button>
                                                            </span>
                                                        );
                                                    })}
                                                
                                                {/* Units */}
                                                {workflowAssignmentInfo[workflow.id]
                                                    .filter(assignment => assignment.organizationalUnitId)
                                                    .map(assignment => {
                                                        // Find the unit to get its type color
                                                        const findUnitWithColor = (units, unitId) => {
                                                            for (const unit of units) {
                                                                if (unit.id === assignment.organizationalUnitId) {
                                                                    return unit;
                                                                }
                                                                if (unit.children && unit.children.length > 0) {
                                                                    const found = findUnitWithColor(unit.children, unitId);
                                                                    if (found) return found;
                                                                }
                                                            }
                                                            return null;
                                                        };
                                                        
                                                        const unit = findUnitWithColor(orgUnits, assignment.organizationalUnitId);
                                                        const unitColor = unit?.unitTypeColor || '#6c757d';
                                                        
                                                        // Adjust colors for dark theme
                                                        const isDarkTheme = theme === 'dark';
                                                        const backgroundColor = isDarkTheme ? `${unitColor}30` : `${unitColor}20`;
                                                        const textColor = isDarkTheme ? `${unitColor}dd` : unitColor;
                                                        const borderColor = isDarkTheme ? `${unitColor}60` : unitColor;
                                                        
                                                        return (
                                                            <span 
                                                                key={assignment.id} 
                                                                className="assignment-tag unit-assignment-tag"
                                                                style={{
                                                                    backgroundColor: backgroundColor,
                                                                    color: textColor,
                                                                    borderColor: borderColor
                                                                }}
                                                            >
                                                                <FaBuilding style={{ fontSize: '0.8rem' }} />
                                                                <span>
                                                                    {assignment.organizationalUnitName || 'Unknown Unit'}
                                                                </span>
                                                                <button 
                                                                    className="btn-close" 
                                                                    onClick={() => handleRemoveAssignment(assignment.id)}
                                                                    title="Remove assignment"
                                                                    style={{ color: textColor }}
                                                                ></button>
                                                            </span>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        ));
    };

    // Handle removing assignment
    const handleRemoveAssignment = async (assignmentId) => {
        try {
            await workflowAssignmentService.delete(assignmentId);
            
            // Refresh all workflow assignments
            await fetchAllAssignments();
            
            toast.success('Assignment removed successfully');
        } catch (err) {
            toast.error('Failed to remove assignment. Please try again.');
        }
    };

    // Function to handle modal close
    const handleModalClose = () => {
        setShowModal(false);
        // Reset modal data
        setModalData({
            type: '',
            sourceId: null,
            sourceName: '',
            targetId: null,
            targetName: '',
            isExcluded: false,
            excludedFromUnitId: null
        });
    };

    // Force text color based on theme
    const textStyle = {
        color: theme === 'dark' ? '#ffffff' : '#333333'
    };

    if (loading) {
        return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Container fluid className="p-4 workflow-assignment-panel">
                <Row>
                    {/* Organizational Units Section */}
                    <Col md={isSmallPanel ? 12 : 4} className={`${isSmallPanel ? 'mb-3' : ''} workflow-column`}>
                        <Card>
                            <Card.Header>
                                <h4 style={textStyle}>{isSmallPanel ? 'Units' : 'Organizational Units'}</h4>
                            </Card.Header>
                            <Card.Body>
                                <div className="search-container mb-3">
                                    <InputGroup>
                                        <FormControl
                                            placeholder="Search units or users..."
                                            value={unitSearch}
                                            onChange={(e) => setUnitSearch(e.target.value)}
                                        />
                                        <InputGroup.Text>
                                            <FaSearch />
                                        </InputGroup.Text>
                                    </InputGroup>
                                </div>
                                <Droppable droppableId="units-container" isDropDisabled={true}>
                                    {(provided) => (
                                        <div 
                                            className="tree-container"
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            {orgUnits && orgUnits.length > 0 ? (
                                                renderUnitTree(orgUnits)
                                            ) : (
                                                <div className="no-data-message">No organizational units found</div>
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </Card.Body>
                        </Card>
                    </Col>
                    
                    {/* Workflows Section */}
                    <Col md={isSmallPanel ? 12 : 8} className="workflow-column">
                        <Card>
                            <Card.Header>
                                <h4 style={textStyle}>Workflows</h4>
                            </Card.Header>
                            <Card.Body>
                                <div className="search-container mb-3">
                                    <InputGroup>
                                        <FormControl
                                            placeholder="Search workflows..."
                                            value={workflowSearch}
                                            onChange={(e) => setWorkflowSearch(e.target.value)}
                                        />
                                        <InputGroup.Text>
                                            <FaSearch />
                                        </InputGroup.Text>
                                    </InputGroup>
                                </div>
                                <div className="list-group">
                                    {workflows && workflows.length > 0 ? (
                                        renderWorkflowList(workflows)
                                    ) : (
                                        <div className="no-data-message">No workflows found</div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
                
            {/* Assignment Modal */}
            {showModal && (
                <AssignmentModal
                    isOpen={showModal}
                    title={modalData.type === 'unit' ? "Assign Unit to Workflow" : "Assign User to Workflow"}
                    onClose={handleModalClose}
                    footer={
                        <div className="access-modal-footer">
                            <button className="btn btn-secondary" onClick={handleModalClose}>
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary" 
                                onClick={handleAssignWorkflow}
                            >
                                Assign Workflow
                            </button>
                        </div>
                    }
                >
                    <div className="access-modal-form">
                        <div className="assignment-title">Assignment Details</div>
                        <div className="assignment-details">
                            <div className="assignment-item">
                                <span className="assignment-label">From:</span>
                                <span className="assignment-value">
                                    {modalData.type === 'unit' ? (
                                        <><FaBuilding style={{ marginRight: '5px' }} /> {modalData.sourceName}</>
                                    ) : (
                                        <><FaUser style={{ marginRight: '5px' }} /> {modalData.sourceName}</>
                                    )}
                                </span>
                            </div>
                            <div className="assignment-arrow">→</div>
                            <div className="assignment-item">
                                <span className="assignment-label">To:</span>
                                <span className="assignment-value">
                                    <FaExchangeAlt style={{ marginRight: '5px' }} /> {modalData.targetName}
                                </span>
                            </div>
                        </div>

                        {modalData.type === 'unit' && (
                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={modalData.isExcluded}
                                        onChange={(e) => setModalData(prev => ({
                                            ...prev,
                                            isExcluded: e.target.checked
                                        }))}
                                    />
                                    Exclude from workflow
                                </label>
                            </div>
                        )}
                    </div>
                </AssignmentModal>
            )}
        </DragDropContext>
    );
};

export default WorkflowAssignment;